import React, { useState, useEffect, useCallback } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import LandingPage from './components/LandingPage';
import PriceForm from './components/PriceForm';
import PredictionResult from './components/PredictionResult';
import NewsSection from './components/NewsSection';
import PricesPanel from './components/PricesPanel';
import AboutPage from './components/Aboutpage';
import TermsPage from './components/Termspage';
import PrivacyPage from './components/PrivacyPage';
import FloatingBlogButton from './components/FloatingBlogButton';
import BlogPage from './components/BlogPage';
import AIBlogGenerator from './components/AIBlogGenerator';
import { useNotifications } from './hooks/useNotifications';
import './App.css';

// ── How often to attempt AI blog generation (check every 30 min at runtime) ──
const GENERATION_CHECK_INTERVAL_MS = 30 * 60 * 1000;

export default function App() {
  const [page, setPage]               = useState('home');
  const [navParams, setNavParams]     = useState({});
  const [predictionData, setPredictionData] = useState(null);
  const [priceMode, setPriceMode]     = useState('student');
  const [aiArticles, setAIArticles]   = useState([]);

  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('naijaagro_user');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  const handleUserChange = (newUser) => {
    if (newUser) localStorage.setItem('naijaagro_user', JSON.stringify(newUser));
    else         localStorage.removeItem('naijaagro_user');
    setUser(newUser);
  };

  // ── Notification system ──────────────────────────────────────────────────
  const handleNewAIArticle = useCallback((article) => {
    setAIArticles(prev => [article, ...prev]);
  }, []);

  const {
    notifications,
    unseenCount,
    unseenBlogCount,
    markAllSeen,
    markSeen,
    addNewsNotification,
    generateAIBlog,
    getAIArticles,
    formatRelative,
  } = useNotifications({ onNewAIArticle: handleNewAIArticle });

  // Load persisted AI articles on mount
  useEffect(() => {
    setAIArticles(getAIArticles());
  }, [getAIArticles]);

  // ── Regulated AI blog generation (checked every 30 min) ─────────────────
  useEffect(() => {
    // Try on mount
    generateAIBlog();
    // Then every 30 min
    const interval = setInterval(generateAIBlog, GENERATION_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [generateAIBlog]);

  // ── Notification click handler ───────────────────────────────────────────
  const handleNotificationClick = useCallback((notif) => {
    markSeen(notif.id);
    if (notif.type === 'blog') {
      setPage('blog');
      setNavParams({ highlightArticleId: notif.articleId });
    } else if (notif.type === 'news') {
      setPage('news');
    }
  }, [markSeen]);

  const togglePriceMode = () => setPriceMode(m => (m === 'student' ? 'live' : 'student'));

  const handlePredict = (data) => { setPredictionData(data); setPage('result'); };

  const handleNavigate = (targetPage, params = {}) => {
    setPage(targetPage);
    setNavParams(params);
    // Clear blog badge when entering blog
    if (targetPage === 'blog' || targetPage === 'ai-blog') markAllSeen();
  };

  // Shared nav props passed to every page
  const navProps = {
    priceMode,
    onModeToggle: togglePriceMode,
    onNavigate: handleNavigate,
    user,
    onUserChange: handleUserChange,
    // Notification props for AppNav
    notifications,
    unseenCount,
    onMarkAllSeen: markAllSeen,
    onNotificationClick: handleNotificationClick,
    formatRelative,
  };

  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID ||
    '230887118107-ppq7bn3ni28tq61nd8chan0s142bg25v.apps.googleusercontent.com';

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <div className="app-root">

        {page === 'home' && (
          <LandingPage
            onPredictClick={() => setPage('predict')}
            onNewsClick={() => setPage('news')}
            onPricesClick={() => setPage('prices')}
            {...navProps}
          />
        )}
        {page === 'predict' && (
          <PriceForm
            onResult={handlePredict}
            onBack={() => { setPage('home'); setNavParams({}); }}
            initialCommodity={navParams.initialCommodity || ''}
            {...navProps}
          />
        )}
        {page === 'result' && (
          <PredictionResult
            data={predictionData}
            onBack={() => setPage('predict')}
            onHome={() => setPage('home')}
            onPricesClick={() => setPage('prices')}
            {...navProps}
          />
        )}
        {page === 'news' && (
          <NewsSection
            onBack={() => setPage('home')}
            onAddNotification={addNewsNotification}
            {...navProps}
          />
        )}
        {page === 'prices' && (
          <PricesPanel onBack={() => setPage('home')} {...navProps} />
        )}
        {page === 'about' && (
          <AboutPage onBack={() => setPage('home')} {...navProps} />
        )}
        {page === 'terms' && (
          <TermsPage onBack={() => setPage('home')} {...navProps} />
        )}
        {page === 'privacy' && (
          <PrivacyPage onBack={() => setPage('home')} {...navProps} />
        )}
        {page === 'blog' && (
          <BlogPage
            onBack={() => setPage('home')}
            aiArticles={aiArticles}
            highlightArticleId={navParams.highlightArticleId}
            {...navProps}
          />
        )}
        {page === 'ai-blog' && (
          <AIBlogGenerator {...navProps} />
        )}

        {/* Floating blog button — hidden on blog / ai-blog pages */}
        {page !== 'blog' && page !== 'ai-blog' && (
          <FloatingBlogButton
            onNavigate={handleNavigate}
            unseenBlogCount={unseenBlogCount}
            lastBlogTimestamp={notifications.find(n => n.type === 'blog')?.timestamp}
          />
        )}

      </div>
    </GoogleOAuthProvider>
  );
}